import { useCallback, useEffect, useRef } from 'react'

import { tMsg } from '@/features/locale/translate'
import type { WalletWriteProgressPhase } from '../wallet/walletSigningFlow'
import type { Web3Library } from '../wallet/walletContext'
import { clearWalletSigningLock, isWalletSigningLockActive } from '../wallet/walletSigningLock'
import { MOBILE_LIQUIDITY_SESSION_EVENT } from './mobileLiquiditySession'
import { isMobileLiquidityWalletWaitActive, readMobileLiquiditySession } from './mobileLiquiditySession'
import { reconcileLiquidityWalletReturn } from './reconcileLiquidityWalletReturn'

const RECONCILE_DEBOUNCE_MS = 800

type UseLiquidityWalletReturnSyncOptions = {
  account?: string | null
  chainId?: number | null
  library?: Web3Library | null
  attemptingTxn: boolean
  walletProgressPhase: WalletWriteProgressPhase | null
  onOrphanedWalletReject?: (message: string) => void
}

function isOrphanedLiquidityWaitState(
  attemptingTxn: boolean,
  walletProgressPhase: WalletWriteProgressPhase | null,
): boolean {
  const session = readMobileLiquiditySession()
  if (isMobileLiquidityWalletWaitActive(session)) return true
  return (
    isWalletSigningLockActive() &&
    (attemptingTxn || walletProgressPhase === 'wallet' || walletProgressPhase === 'submitted')
  )
}

export function useLiquidityWalletReturnSync({
  account,
  chainId,
  library,
  attemptingTxn,
  walletProgressPhase,
  onOrphanedWalletReject,
}: UseLiquidityWalletReturnSyncOptions) {
  const hiddenDuringSigningRef = useRef(false)
  const inFlightRef = useRef<Promise<unknown> | null>(null)
  const reconcileGenerationRef = useRef(0)
  const attemptingTxnRef = useRef(attemptingTxn)
  const walletProgressPhaseRef = useRef(walletProgressPhase)
  const accountRef = useRef(account)
  const chainIdRef = useRef(chainId)
  const libraryRef = useRef(library)
  const onOrphanedRejectRef = useRef(onOrphanedWalletReject)

  attemptingTxnRef.current = attemptingTxn
  walletProgressPhaseRef.current = walletProgressPhase
  accountRef.current = account
  chainIdRef.current = chainId
  libraryRef.current = library
  onOrphanedRejectRef.current = onOrphanedWalletReject

  const trackInFlight = useCallback(<T,>(promise: Promise<T>): Promise<T> => {
    inFlightRef.current = promise
    void promise.finally(() => {
      if (inFlightRef.current === promise) {
        inFlightRef.current = null
      }
    })
    return promise
  }, [])

  const isSigningContext = useCallback(() => {
    return (
      isWalletSigningLockActive() ||
      isOrphanedLiquidityWaitState(attemptingTxnRef.current, walletProgressPhaseRef.current)
    )
  }, [])

  const runAfterReturn = useCallback(async () => {
    const generation = ++reconcileGenerationRef.current

    const result = await reconcileLiquidityWalletReturn({
      library: libraryRef.current,
      account: accountRef.current,
      chainId: chainIdRef.current,
      attemptingTxn: attemptingTxnRef.current,
      walletProgressPhase: walletProgressPhaseRef.current,
      hasPromiseInFlight: Boolean(inFlightRef.current),
    })

    if (generation !== reconcileGenerationRef.current) return

    if (result === 'recovered-hash') {
      clearWalletSigningLock()
      window.dispatchEvent(new CustomEvent(MOBILE_LIQUIDITY_SESSION_EVENT))
      return
    }

    if (
      inFlightRef.current ||
      !onOrphanedRejectRef.current ||
      !isWalletSigningLockActive()
    ) {
      return
    }

    if (!isOrphanedLiquidityWaitState(attemptingTxnRef.current, walletProgressPhaseRef.current)) {
      return
    }

    onOrphanedRejectRef.current(tMsg().wallet.error.userRejected)
  }, [])

  const scheduleAfterReturn = useCallback(() => {
    const generation = reconcileGenerationRef.current
    window.setTimeout(() => {
      if (generation !== reconcileGenerationRef.current) return
      void runAfterReturn()
    }, RECONCILE_DEBOUNCE_MS)
  }, [runAfterReturn])

  useEffect(() => {
    if (!isSigningContext()) return
    scheduleAfterReturn()
  }, [account, chainId, isSigningContext, library, scheduleAfterReturn])

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (isSigningContext()) {
          hiddenDuringSigningRef.current = true
        }
        return
      }

      if (!hiddenDuringSigningRef.current) return
      hiddenDuringSigningRef.current = false
      scheduleAfterReturn()
    }

    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [isSigningContext, scheduleAfterReturn])

  return { trackInFlight }
}
